// ─────────────────────────────────────────────────────────────────────────────
// AIR DUCT TX — the two diagrams, in the LANES language (skill://diagrams §5).
//
// Vocabulary copied from the house template, content swapped. Two diagrams with
// identical grammar so they read as before/after:
//
//   buildBaseline()    — how Air Duct runs today. No AI anywhere.
//   buildTailorMind()  — the same operation with the Tailormind box in it.
//
// Both are React Flow nodes on the BOARD'S OWN canvas. Never nest a <ReactFlow>
// inside a board node — the outer instance drops to zero nodes and the board
// renders blank with no console error (skill://diagrams §4).
//
// Law §11: a diagram wears the board's chrome — a white panel inside a gray
// container with ONE black pill label — so several diagrams on one board read
// as identical rectangles differing only in content.
//
// Everything here traces to meetings 89/90. Nothing is invented, and anything we
// do not know is labelled as unknown rather than guessed.
// ─────────────────────────────────────────────────────────────────────────────

import type { Node, Edge, NodeProps } from "@xyflow/react";
import { Handle, Position, MarkerType } from "@xyflow/react";

const F = 'ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const MONO = '"SFMono-Regular", Menlo, Consolas, "PT Mono", Courier, monospace';

// Every node type gets ports, including the ones that are just a rectangle: a
// container with no <Handle> silently swallows every edge aimed at it.
const Ports = () => (<>
  <Handle type="target" id="l" position={Position.Left} style={{ opacity: 0, width: 1, height: 1 }} />
  <Handle type="source" id="r" position={Position.Right} style={{ opacity: 0, width: 1, height: 1 }} />
  <Handle type="target" id="t" position={Position.Top} style={{ opacity: 0, width: 1, height: 1 }} />
  <Handle type="source" id="b" position={Position.Bottom} style={{ opacity: 0, width: 1, height: 1 }} />
</>);

// Notion palette (Erez, 2026-07-28). Structure is unchanged — skill://diagrams
// governs the LAYOUT (lanes, one container, colour per card, no borders); this
// is only the pigment. Notion's tints are lighter than what was here, so the
// cards read quieter and the hue still carries the classification.
export const L = {
  ink: "#37352F", ink2: "#787774", ink3: "#9B9A97",
  hair: "#E9E9E7", white: "#FFFFFF",
  blue: "#E7F3F8", blueEdge: "#C9DDE8", blueInk: "#0B6E99",
  green: "#EDF3EC", greenInk: "#0F7B6C",
  purple: "#F6F3F9", purpleInk: "#6940A5",
  orange: "#FAEBDD", orangeInk: "#D9730D",
  yellow: "#FBF3DB", yellowInk: "#CB912F",
  teal: "#DDEDEA", tealInk: "#0F7B6C",
  gate: "#37352F", gateInk: "#FFFFFF",
};

const HUE = {
  white:  { bg: L.white,  ink: L.ink },
  green:  { bg: L.green,  ink: L.greenInk },
  purple: { bg: L.purple, ink: L.purpleInk },
  orange: { bg: L.orange, ink: L.orangeInk },
  yellow: { bg: L.yellow, ink: L.yellowInk },
  teal:   { bg: L.teal,   ink: L.tealInk },
  blue:   { bg: L.blue,   ink: L.blueInk },
  gate:   { bg: L.gate,   ink: L.gateInk },
} as const;

/** A lane name — plain text above the column. Not a box. */
function LaneLabel({ data }: NodeProps) {
  const d = data as unknown as { text: string; sub?: string };
  return (
    <div style={{ width: 620, fontFamily: F }}>
      <div style={{ fontSize: 20, fontWeight: 600, color: L.ink, letterSpacing: "-.01em" }}>
        {d.text}{d.sub && <span style={{ color: L.ink3, fontWeight: 500 }}> {d.sub}</span>}
      </div>
      <Ports />
    </div>
  );
}

/** A small bold label for a secondary flow. */
function FlowLabel({ data }: NodeProps) {
  const d = data as unknown as { text: string; sub?: string };
  return (
    <div style={{ fontFamily: F, width: 900 }}>
      <span style={{ fontSize: 17, fontWeight: 600, color: L.ink }}>{d.text}</span>
      {d.sub && <span style={{ fontSize: 14, color: L.ink3, marginLeft: 8 }}>{d.sub}</span>}
      <Ports />
    </div>
  );
}

/** The ONE container. Used only where a real boundary exists. */
function Container({ data }: NodeProps) {
  const d = data as unknown as { title: string; sub?: string; w: number; h: number };
  return (
    <div style={{ width: d.w, height: d.h, background: L.blue, borderRadius: 3, fontFamily: F,
      boxShadow: `inset 0 0 0 1.5px ${L.blueEdge}`, paddingTop: 14, boxSizing: "border-box" }}>
      <div style={{ textAlign: "center", fontSize: 16.5, fontWeight: 600, color: L.blueInk }}>{d.title}</div>
      {d.sub && <div style={{ textAlign: "center", fontSize: 12, color: L.blueInk, opacity: .7, marginTop: 3 }}>{d.sub}</div>}
      <Ports />
    </div>
  );
}

/** How a use case is DELIVERED. Erez promised these two axes in the room:
 *  a PROGRAM (deterministic, can only do its one action) vs an AGENT, and
 *  AUTOMATED (runs itself) vs COLLABORATIVE (a human is in the loop). */
const TAG = {
  program: { bg: "#DDE9F8", ink: "#1E3E63" },
  agent:   { bg: "#DCCFF5", ink: "#3F2A72" },
  auto:    { bg: "#C7EBD5", ink: "#14532D" },
  collab:  { bg: "#F7EDBB", ink: "#6B5A0B" },
  cond:    { bg: "#2E3A45", ink: "#FFFFFF" },
  kept:    { bg: "#ECEFF3", ink: "#6B7B88" },
  fresh:   { bg: "#14532D", ink: "#FFFFFF" },
} as const;

function Tags({ tags }: { tags?: { t: string; k: keyof typeof TAG }[] }) {
  if (!tags?.length) return null;
  return (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 8 }}>
      {tags.map((g) => (
        <span key={g.t} style={{ background: TAG[g.k].bg, color: TAG[g.k].ink, fontFamily: MONO,
          fontSize: 9.5, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase",
          padding: "3px 7px", borderRadius: 3, whiteSpace: "nowrap" }}>{g.t}</span>
      ))}
    </div>
  );
}

/** A card. White by default; a hue when it needs classifying. */
function LCard({ data }: NodeProps) {
  const d = data as unknown as {
    label: string; sub?: string; bullets?: string[]; w: number;
    tone?: keyof typeof HUE; center?: boolean; small?: boolean;
    tags?: { t: string; k: keyof typeof TAG }[];
  };
  const h = HUE[d.tone ?? "white"];
  return (
    <div data-zoom style={{ width: d.w, background: h.bg, borderRadius: 3, fontFamily: F, boxSizing: "border-box",
      padding: d.small ? "9px 12px" : "12px 14px",
      boxShadow: h.bg === L.white ? `inset 0 0 0 1px ${L.hair}` : "none" }}>
      <div style={{ fontSize: d.small ? 13 : 15.5, fontWeight: 600, color: h.ink, textAlign: d.center ? "center" : "left", lineHeight: 1.25 }}>{d.label}</div>
      {d.sub && <div style={{ fontSize: d.small ? 11 : 12.5, color: h.ink, opacity: .72, marginTop: 3, textAlign: d.center ? "center" : "left", lineHeight: 1.35 }}>{d.sub}</div>}
      {d.bullets && (
        <ul style={{ margin: "8px 0 0", paddingLeft: 17 }}>
          {d.bullets.map((b) => <li key={b} style={{ fontSize: 13, color: h.ink, opacity: .88, lineHeight: 1.55 }}>{b}</li>)}
        </ul>
      )}
      <Tags tags={d.tags} />
      <Ports />
    </div>
  );
}

function LTitle({ data }: NodeProps) {
  const d = data as unknown as { title: string; sub?: string; w: number };
  return (
    <div style={{ width: d.w, fontFamily: F }}>
      <div style={{ fontSize: 40, fontWeight: 500, color: L.ink, letterSpacing: "-.02em" }}>{d.title}</div>
      {d.sub && <div style={{ fontSize: 17, color: L.ink2, marginTop: 10, maxWidth: 1500, lineHeight: 1.5 }}>{d.sub}</div>}
      <Ports />
    </div>
  );
}

// §11 — a diagram wears the board's chrome. These are SectionBox's real values,
// not an approximation: gray #D2D2D2, 1px #BEBEBE, radius 48, padding 210, and
// a 112px black pill. My first pass invented its own (radius 26, 26px label,
// padding 46) and it read as a foreign object next to the deck and document
// sections — Erez called it, correctly.
export const PAD = 210;        // gray padding, matches SectionBox
export const INNER = 230;      // breathing room INSIDE the white panel — this is
                               // what makes the white area big; the gray stays at
                               // the board standard of 210.
export const LABEL_BLOCK = 230; // the pill plus the gap under it

/** The board chrome around a diagram: gray box, one black pill, white panel. */
export function DiagramFrame({ data }: NodeProps) {
  const d = data as unknown as { label: string; w: number; h: number; bare?: boolean };
  return (
    <div style={{ width: d.w, height: d.h, background: "#D2D2D2", border: "1px solid #BEBEBE",
      borderRadius: 48, boxSizing: "border-box", padding: PAD, position: "relative", fontFamily: F }}>
      <div style={{ display: "inline-block", background: "#1C1917", color: "#FFFFFF",
        fontSize: 112, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.06,
        padding: "16px 52px", borderRadius: 22 }}>{d.label}</div>
      {/* bare = the diagrams supply their own white panels side by side */}
      {!d.bare && <div style={{ position: "absolute", left: PAD, right: PAD, top: PAD + LABEL_BLOCK, bottom: PAD,
        background: "#FFFFFF", borderRadius: 28 }} />}
      <Ports />
    </div>
  );
}

/** One white sheet behind one diagram. Three of these sit side by side inside
 *  the single gray container, with real space between them (Erez, 2026-07-27). */
function WhitePanel({ data }: NodeProps) {
  const d = data as unknown as { w: number; h: number };
  return (
    <div style={{ width: d.w, height: d.h, background: "#FFFFFF", borderRadius: 28 }}>
      <Ports />
    </div>
  );
}

export const airDuctLaneNodeTypes = {
  adpanel: WhitePanel,
  adlane: LaneLabel, adflow: FlowLabel, adcontainer: Container,
  adcard: LCard, adtitle: LTitle, adframe: DiagramFrame,
};

// ── builders ────────────────────────────────────────────────────────────────
type O = { text?: string; sh?: string; th?: string; dark?: boolean; dash?: boolean };

function mk(pre: string, ox: number, oy: number) {
  const N = (id: string, type: string, x: number, y: number, data: Record<string, unknown>, z = 3): Node =>
    ({ id: `${pre}-${id}`, type, position: { x: x + ox, y: y + oy }, draggable: false, selectable: false, zIndex: z, data });
  const E = (id: string, s: string, t: string, o: O = {}): Edge => {
    const stroke = o.dark ? L.ink : "#9AA7B2";
    return { id: `${pre}-${id}`, source: `${pre}-${s}`, target: `${pre}-${t}`,
      sourceHandle: o.sh ?? "r", targetHandle: o.th ?? "l", type: "smoothstep", pathOptions: { borderRadius: 10 },
      style: { stroke, strokeWidth: o.dark ? 2 : 1.5, strokeDasharray: o.dash ? "6 5" : undefined },
      markerEnd: { type: MarkerType.ArrowClosed, color: stroke, width: 12, height: 12 },
      label: o.text, labelShowBg: !!o.text,
      labelStyle: { fontFamily: F, fontSize: 11.5, fontWeight: 600, fill: o.dark ? L.ink : L.ink2 },
      labelBgStyle: { fill: "#FFFFFF" }, labelBgPadding: [5, 2] as [number, number], labelBgBorderRadius: 3, zIndex: 2 } as Edge;
  };
  return { N, E };
}

// ── 1 · THE BASELINE — how they run today ───────────────────────────────────
export function buildBaseline(ox: number, oy: number): { nodes: Node[]; edges: Edge[] } {
  const { N, E } = mk("bl", ox, oy);
  const nodes: Node[] = [
    N("frame", "adframe", -(PAD + INNER), -(PAD + LABEL_BLOCK + INNER), { label: "1 · TODAY", w: 1860 + 2 * (PAD + INNER), h: 1000 + PAD * 2 + LABEL_BLOCK + INNER * 2 }, 0),
    N("t", "adtitle", 0, -60, { w: 1600, title: "Six systems, joined by hand.",
      sub: "Every box is something they described on 23 July. The dark cards are the three places the wiring is simply not there — and each one is one of the six pains they ranked." }, 8),

    N("l1", "adlane", 0, 70, { text: "How work arrives", sub: "· demand + capture" }, 6),
    N("l2", "adlane", 700, 70, { text: "Operations", sub: "· one system of record" }, 6),
    N("l3", "adlane", 1420, 70, { text: "After the job", sub: "· money + reporting" }, 6),

    // LANE 1
    N("agg", "adcard", 0, 120, { label: "Lead aggregators", sub: "the usual paid sources", w: 260 }),
    N("num", "adcard", 0, 196, { label: "Branded numbers", sub: "every campaign its own", w: 260 }),
    N("form", "adcard", 0, 272, { label: "Web forms", sub: "reshaped into a Google Form", w: 260 }),
    N("cr", "adcard", 310, 150, { label: "CallRail", sub: "call tracking · UTMs", w: 230, tone: "teal" }),
    N("cy", "adcard", 310, 240, { label: "Callingly", sub: "5s contact · they want off it", w: 230, tone: "orange" }),
    N("glue", "adcard", 310, 330, { label: "Zapier / n8n", sub: "hand-built · no native integrations", w: 230, tone: "orange" }),

    // LANE 2 — the ONE container: one system, one boundary
    N("wz", "adcontainer", 700, 120, { title: "Workiz", sub: "field-service management · the system of record", w: 560, h: 430 }, 2),
    N("sch", "adcard", 724, 186, { label: "Scheduling", sub: "manual — nothing surfaces real availability", w: 512, center: true }),
    N("dis", "adcard", 724, 262, { label: "Dispatch + routing", w: 512, center: true }),
    N("job", "adcard", 724, 322, { label: "Jobs", sub: "the record of work done", w: 512, center: true }),
    N("sip", "adcard", 724, 398, { label: "SOFTPHONE — CLOSED", sub: "no interface out · pain 3", w: 512, center: true, tone: "gate" }, 4),
    N("ai", "adcard", 700, 590, { label: "AI answering service", sub: "overflow calls · books · moves the schedule", w: 560 }),

    // LANE 3
    N("comm", "adcard", 1420, 120, { label: "Commissions + payroll", w: 340, tone: "orange",
      bullets: ["~30 on commission, ~25 on payroll", "pulled from several places", "reconciled in spreadsheets", "two days, every cycle"] }),
    N("bq", "adcard", 1420, 300, { label: "BigQuery", w: 340, tone: "green",
      bullets: ["only the CRM lead lifecycle", "loaded by the hand-built glue"] }),
    N("lk", "adcard", 1420, 424, { label: "Data Studio / Looker", w: 340, tone: "purple",
      bullets: ["“not married to it in any kind of way”", "end-period beside cohorted"] }),
    N("own", "adcard", 1420, 560, { label: "The owner", sub: "decides late, and does not trust the number", w: 340, center: true, tone: "gate" }, 4),

    // the three breaks — the loudest thing on the page
    N("brk1", "adcard", 1090, 690, { label: "NO CALL METRICS", sub: "no handle time · no abandoned rate", w: 260, tone: "gate", small: true }, 4),
    N("brk2", "adcard", 1090, 762, { label: "PAYROLL NEVER ARRIVES", sub: "the cycle reaches no report", w: 260, tone: "gate", small: true }, 4),
    N("brk3", "adcard", 1090, 834, { label: "A COLUMN MOVES, IT BREAKS", sub: "quietly · nobody is told", w: 260, tone: "gate", small: true }, 4),

    // secondary flow — one job, end to end
    N("fl", "adflow", 0, 700, { text: "One job, end to end", sub: "— and where it goes dark" }, 6),
    N("s1", "adcard", 0, 740, { label: "lead", sub: "aggregator or call", w: 128, small: true }),
    N("s2", "adcard", 158, 740, { label: "answer", sub: "agent or the AI", w: 128, small: true }),
    N("s3", "adcard", 316, 740, { label: "book", sub: "by hand", w: 118, small: true }),
    N("s4", "adcard", 464, 740, { label: "dispatch", sub: "routed", w: 118, small: true }),
    N("s5", "adcard", 612, 740, { label: "the job", sub: "never described", w: 138, small: true, tone: "yellow" }),
    N("s6", "adcard", 780, 740, { label: "invoice", sub: "never described", w: 138, small: true, tone: "yellow" }),
    N("s7", "adcard", 0, 828, { label: "commission by hand", sub: "two days a cycle", w: 200, small: true, tone: "orange" }),
    N("s8", "adcard", 230, 828, { label: "a report", sub: "late, and doubted", w: 160, small: true, tone: "purple" }),
  ];

  const edges: Edge[] = [
    E("1", "agg", "cr"), E("2", "num", "cr"), E("3", "form", "glue"),
    E("4", "cr", "cy", { sh: "b", th: "t" }), E("5", "cy", "glue", { sh: "b", th: "t" }),
    E("6", "cr", "wz"), E("7", "glue", "wz"),
    E("8", "ai", "wz", { sh: "t", th: "b", text: "books · moves" }),
    E("9", "wz", "comm", { text: "downloaded by hand" }),
    E("10", "wz", "bq", { text: "via the glue" }),
    E("11", "bq", "lk", { sh: "b", th: "t" }), E("12", "lk", "own", { sh: "b", th: "t" }),
    // the breaks
    E("b1", "sip", "brk1", { sh: "b", th: "l", dark: true, dash: true }),
    E("b2", "comm", "brk2", { sh: "b", th: "r", dark: true, dash: true }),
    E("b3", "glue", "brk3", { sh: "b", th: "l", dark: true, dash: true }),
    // the strip
    E("f1", "s1", "s2"), E("f2", "s2", "s3"), E("f3", "s3", "s4"), E("f4", "s4", "s5"), E("f5", "s5", "s6"),
    E("f6", "s6", "s7", { sh: "b", th: "t" }), E("f7", "s7", "s8"),
  ];
  return { nodes, edges };
}

// ── 2 · WITH TAILORMIND ─────────────────────────────────────────────────────
export function buildTailorMind(ox: number, oy: number): { nodes: Node[]; edges: Edge[] } {
  const { N, E } = mk("tm", ox, oy);
  const nodes: Node[] = [
    N("frame", "adframe", -(PAD + INNER), -(PAD + LABEL_BLOCK + INNER), { label: "2 · AFTER", w: 1980 + 2 * (PAD + INNER), h: 1030 + PAD * 2 + LABEL_BLOCK + INNER * 2 }, 0),
    N("t", "adtitle", 0, -60, { w: 1600, title: "Same systems. One box in the middle.",
      sub: "They keep every tool they already pay for — the left column is unchanged. What is new is one small server they own, and the two dark gates, because nothing writes anywhere without passing one." }, 8),

    N("l1", "adlane", 0, 70, { text: "Their systems + their people", sub: "· unchanged" }, 6),
    N("l2", "adlane", 700, 70, { text: "One box they own", sub: "· Tailormind" }, 6),
    N("l3", "adlane", 1420, 70, { text: "The brain, and what they see", sub: "" }, 6),

    // LANE 1
    N("wz", "adcard", 0, 120, { label: "Workiz", sub: "jobs · schedule · calls", w: 260, tags: [{ t: "kept", k: "kept" }] }),
    N("cr", "adcard", 0, 200, { label: "CallRail", sub: "tracking · UTMs", w: 260, tags: [{ t: "kept", k: "kept" }] }),
    N("src", "adcard", 0, 280, { label: "Forms + aggregators", w: 260, tags: [{ t: "kept", k: "kept" }] }),
    N("ads", "adcard", 0, 356, { label: "Ad platforms", sub: "spend", w: 260, tags: [{ t: "kept", k: "kept" }] }),
    N("pay", "adcard", 0, 436, { label: "Payroll + books", sub: "if reachable — question 1", w: 260, tone: "orange", tags: [{ t: "kept", k: "kept" }] }),
    N("owner", "adcard", 0, 500, { label: "The owner", sub: "asks it questions", w: 260, center: true }),
    N("ops", "adcard", 0, 576, { label: "Operations", sub: "approves the run", w: 260, center: true }),
    N("crew", "adcard", 0, 652, { label: "Crews + CSRs", sub: "touched indirectly, at first", w: 260, center: true }),
    N("con", "adcard", 330, 540, { label: "Console", sub: "the door · per-person permissions", w: 210, center: true, tone: "teal" }),

    // LANE 2 — the ONE container
    N("box", "adcontainer", 700, 120, { title: "Tailormind — one small server", sub: "their account · their data · their IP", w: 560, h: 600 }, 2),
    N("conn", "adcard", 724, 186, { label: "Connectors + drift watch", sub: "a renamed column is caught and repaired · pain 6", w: 512, center: true , tags: [{ t: "new", k: "fresh" }] }),
    N("agent", "adcard", 724, 272, { label: "Agent runtime", sub: "answers questions, cites the rows it used", w: 512, center: true , tags: [{ t: "new", k: "fresh" }] }),
    N("tools", "adcard", 724, 358, { label: "Typed tools", sub: "every NUMBER computed in SQL, never guessed", w: 512, center: true , tags: [{ t: "new", k: "fresh" }] }),
    N("wf", "adcard", 724, 444, { label: "Workflow engine", sub: "the commission rules as a frozen spec — a program, not an agent", w: 512, center: true , tags: [{ t: "new", k: "fresh" }] }),
    N("mon", "adcard", 724, 546, { label: "Monitors", sub: "what went quiet, what drifted", w: 512, center: true , tags: [{ t: "new", k: "fresh" }] }),
    N("out", "adcard", 724, 622, { label: "Outbox", sub: "ordered · retried", w: 512, center: true , tags: [{ t: "new", k: "fresh" }] }),

    // the gates — the one accent
    N("wg", "adcard", 1300, 200, { label: "WRITE GATE", sub: "provenance", w: 108, tone: "gate", center: true, small: true }, 4),
    N("og", "adcard", 1300, 520, { label: "THE GATE", sub: "draft first", w: 108, tone: "gate", center: true, small: true }, 4),

    // LANE 3
    N("pg", "adcard", 1420, 120, { label: "One copy of the data", w: 340, tone: "green",
      bullets: ["their account · their data", "every number traceable to a row", "sliceable by department", "ready for the acquisition"] }),
    N("sem", "adcard", 1420, 300, { label: "The definitions", w: 340, tone: "blue",
      bullets: ["a job · a lead · a source · revenue", "each declares cohort or period", "agreed once, applied everywhere", "this is the fix for pain 1"] }),
    N("audit", "adcard", 1420, 480, { label: "Audit + spend", w: 340, tone: "orange",
      bullets: ["append-only log of every action", "a cap that fails closed"] }),
    N("see", "adcard", 1420, 610, { label: "What they open", w: 340, tone: "purple",
      bullets: ["ask in plain language", "boards that compare like with like", "the commission run", "system health · what repaired itself"] }),

    // The three BREAK cards from diagram 1, in the same lane, now resolved.
    // Dark in TODAY, green in AFTER — that pairing is what makes the two
    // diagrams legible as before/after (Erez, 2026-07-26).
    N("fx", "adflow", 1420, 800, { text: "The three breaks from diagram 1", sub: "— resolved" }, 6),
    N("fx1", "adcard", 1420, 842, { label: "Call metrics now visible", sub: "as far as the tracking layer reaches", w: 340, tone: "green", small: true }),
    N("fx2", "adcard", 1420, 898, { label: "Payroll reaches reporting", sub: "the cycle lands in the warehouse", w: 340, tone: "green", small: true }),
    N("fx3", "adcard", 1420, 954, { label: "A moved column self-heals", sub: "caught, repaired, and you are told", w: 340, tone: "green", small: true }),

    // secondary flows — the use cases
    N("fl1", "adflow", 0, 790, { text: "Phase 1 · connect and see", sub: "— the visibility they asked for" }, 6),
    N("a1", "adcard", 0, 830, { label: "connect", sub: "read-only", w: 130, small: true }),
    N("a2", "adcard", 160, 830, { label: "normalise", sub: "one shape", w: 130, small: true }),
    N("a3", "adcard", 320, 830, { label: "agree definitions", sub: "with them", w: 168, small: true, tone: "blue" }),
    N("a4", "adcard", 518, 830, { label: "one copy", sub: "they own it", w: 140, small: true, tone: "green" }),
    N("a5", "adcard", 688, 830, { label: "they see it", sub: "truthfully", w: 150, small: true, tone: "purple" }),

    N("fl2", "adflow", 900, 790, { text: "Phase 3 · the commission run", sub: "— two days becomes minutes" }, 6),
    N("b1", "adcard", 900, 830, { label: "pull the cycle", sub: "jobs + rates", w: 148, small: true }),
    N("b2", "adcard", 1078, 830, { label: "apply the rules", sub: "a program", w: 148, small: true }),
    N("b3", "adcard", 1256, 830, { label: "flag exceptions", sub: "the last 10%", w: 154, small: true, tone: "yellow" }),
    N("b4", "adcard", 1440, 830, { label: "THE GATE", sub: "a human approves", w: 148, small: true, tone: "gate" }),
    N("b5", "adcard", 1618, 830, { label: "export", sub: "to payroll", w: 130, small: true, tone: "green" }),

    N("fl3", "adflow", 0, 920, { text: "Any time · when Workiz moves a column", sub: "— the self-healing they asked for" }, 6),
    N("c1", "adcard", 0, 960, { label: "drift detected", sub: "the watch sees it", w: 150, small: true }),
    N("c2", "adcard", 180, 960, { label: "mapping repaired", w: 160, small: true }),
    N("c3", "adcard", 370, 960, { label: "rows re-synced", w: 150, small: true }),
    N("c4", "adcard", 550, 960, { label: "they are told", sub: "not a dead dashboard days later", w: 220, small: true, tone: "teal" }),
  ];

  const edges: Edge[] = [
    E("1", "wz", "box"), E("2", "cr", "box"), E("3", "src", "box"), E("4", "ads", "box"),
    E("5", "pay", "box", { dash: true, text: "if reachable" }),
    E("6", "owner", "con", { sh: "b", th: "l" }), E("7", "ops", "con"), E("8", "crew", "con", { sh: "t", th: "l" }),
    E("9", "con", "box", { text: "one door" }),
    E("10", "conn", "wg", { dark: true }), E("11", "wg", "pg", { dark: true, text: "verified" }),
    E("12", "tools", "sem", { text: "reads" }),
    E("13", "wf", "og", { dark: true }), E("14", "og", "audit", { dark: true, text: "logged" }),
    E("15", "og", "wz", { dark: true, dash: true, sh: "b", th: "b", text: "writes back only through the gate" }),
    E("16", "out", "see", { text: "and it reaches them" }),
    // the three flows
    E("p1", "a1", "a2"), E("p2", "a2", "a3"), E("p3", "a3", "a4"), E("p4", "a4", "a5"),
    E("q1", "b1", "b2"), E("q2", "b2", "b3"), E("q3", "b3", "b4"), E("q4", "b4", "b5"),
    E("r1", "c1", "c2"), E("r2", "c2", "c3"), E("r3", "c3", "c4"),
  ];
  return { nodes, edges };
}

// ── 3 · EVERY PROBLEM → ITS CAUSE → WHAT WE BUILD ───────────────────────────
// All-inclusive on purpose: every problem they raised on 23 July, not only the
// six John ranked. Twenty problems collapse into FIVE causes, and that
// collapse is the whole scoping argument — it is why this is one foundation
// plus a handful of use cases rather than twenty projects.
//
// Every solution is classified on the two axes Erez promised in the room:
// PROGRAM (deterministic, can only ever do its one action) vs AGENT, and
// AUTOMATED vs COLLABORATIVE (a human in the loop).
export function buildUseCases(ox: number, oy: number, frame = true): { nodes: Node[]; edges: Edge[] } {
  const { N, E } = mk("uc", ox, oy);
  const nodes: Node[] = [
    ...(frame ? [N("frame", "adframe", -(PAD + INNER), -(PAD + LABEL_BLOCK + INNER), { label: "3 · WHY", w: 2280 + 2 * (PAD + INNER), h: 1560 + PAD * 2 + LABEL_BLOCK + INNER * 2 }, 0)] : []),
    N("t", "adtitle", 0, -60, { w: 1700, title: "Twenty problems. Five causes. Eleven things to build.",
      sub: "Everything they raised on 23 July, not only the six they ranked. The collapse in the middle is the argument: this is one foundation plus a handful of use cases, not twenty projects. Each build is tagged by HOW it is delivered — a program can only ever do its one action; an agent is used only where judgement is genuinely needed." }, 8),

    N("l1", "adlane", 0, 80, { text: "What they told us", sub: "· every problem raised" }, 6),
    N("l2", "adlane", 800, 80, { text: "The root cause", sub: "· our analysis" }, 6),
    N("l3", "adlane", 1440, 80, { text: "What we would build", sub: "· and how it is delivered" }, 6),

    // ── LANE 1 · the problems, grouped by the cause they belong to ──
    N("p1", "adcard", 0, 130, { label: "Reporting is delayed, inaccurate, or counterintuitive", w: 300, small: true }),
    N("p2", "adcard", 0, 196, { label: "End-period beside cohorted gives a false positive", w: 300, small: true }),
    N("p3", "adcard", 0, 262, { label: "Decisions get delayed, and the cost compounds", w: 300, small: true }),

    N("p4", "adcard", 0, 348, { label: "Data scattered across every system", w: 300, small: true }),
    N("p5", "adcard", 0, 414, { label: "Wants every data point from every software, in one place", w: 300, small: true }),
    N("p6", "adcard", 0, 490, { label: "Needs department slicing — they are acquiring", w: 300, small: true }),
    N("p7", "adcard", 0, 556, { label: "No native integrations — Zapier and n8n hold it together", w: 300, small: true }),
    N("p8", "adcard", 0, 632, { label: "They want off Callingly", w: 300, small: true }),
    N("p9", "adcard", 0, 688, { label: "The last system was never theirs, and it left", w: 300, small: true }),

    N("p10", "adcard", 0, 774, { label: "Scheduling is manual — jobs get lost or forgotten", w: 300, small: true }),
    N("p11", "adcard", 0, 840, { label: "The calendar overbooks", w: 300, small: true }),
    N("p12", "adcard", 0, 896, { label: "After-hours bookings cannot see the real schedule", w: 300, small: true }),
    N("p13", "adcard", 0, 962, { label: "Nothing handles a crew running late", w: 300, small: true }),
    N("p14", "adcard", 0, 1018, { label: "Commissions + payroll: two days a cycle, by hand", w: 300, small: true }),
    N("p15", "adcard", 0, 1084, { label: "Workiz renames a column and it breaks quietly", w: 300, small: true }),

    N("p16", "adcard", 0, 1170, { label: "No handle time, no abandoned rate", w: 300, small: true }),
    N("p17", "adcard", 0, 1226, { label: "No clear picture of the call centre at all", w: 300, small: true }),

    N("p18", "adcard", 0, 1312, { label: "Fear of an agent writing transactions into the books", w: 300, small: true }),
    N("p19", "adcard", 0, 1378, { label: "“How do you keep dangerous people from being dangerous”", w: 300, small: true }),
    N("p20", "adcard", 0, 1454, { label: "AI is contextual, humans are abstract — an education gap", w: 300, small: true }),

    // ── LANE 2 · five causes ──
    N("r1", "adcard", 800, 170, { label: "No agreed definitions", w: 350, tone: "blue",
      bullets: ["a job, a lead, a source mean different things", "so two correct numbers disagree", "a charting tool cannot fix this"] }),
    N("r2", "adcard", 800, 420, { label: "No data model they own", w: 350, tone: "blue",
      bullets: ["six systems, hand-built glue between them", "nothing owns the whole picture", "and nothing they build stays theirs"] }),
    N("r3", "adcard", 800, 800, { label: "Facts never written down as data", w: 350, tone: "blue",
      bullets: ["capacity, drive time, job duration", "commission rules live in a head", "so nothing can compute them"] }),
    N("r4", "adcard", 800, 1150, { label: "A closed third-party product", w: 350, tone: "gate",
      bullets: ["the softphone has no interface out", "the ONLY cause not ours to fix", "scope it honestly, or not at all"] }),
    N("r5", "adcard", 800, 1360, { label: "No guardrail or permission layer", w: 350, tone: "blue",
      bullets: ["nothing today decides what may write", "and nobody has been taught the edges"] }),

    // ── LANE 3 · what we build, tagged by delivery ──
    N("s1", "adcard", 1440, 130, { label: "The definitions catalogue", w: 420, tone: "green",
      sub: "each metric declares what it counts and on what basis, agreed once and applied everywhere",
      tags: [{ t: "program", k: "program" }, { t: "collaborative", k: "collab" }] }),
    N("s2", "adcard", 1440, 268, { label: "Honest boards", w: 420, tone: "green",
      sub: "cohort and period never drawn as equals",
      tags: [{ t: "program", k: "program" }, { t: "automated", k: "auto" }] }),
    N("s3", "adcard", 1440, 380, { label: "Ask in plain language", w: 420, tone: "purple",
      sub: "the interface they already proved, citing the rows it used",
      tags: [{ t: "agent", k: "agent" }, { t: "collaborative", k: "collab" }] }),
    N("s4", "adcard", 1440, 500, { label: "Connectors + one warehouse", w: 420, tone: "green",
      sub: "their account, their data, sliceable by department",
      tags: [{ t: "program", k: "program" }, { t: "automated", k: "auto" }] }),
    N("s5", "adcard", 1440, 620, { label: "Schema-drift watch + repair", w: 420, tone: "green",
      sub: "catch the renamed column, repair the mapping, re-sync, say so",
      tags: [{ t: "program", k: "program" }, { t: "automated", k: "auto" }] }),
    N("s6", "adcard", 1440, 740, { label: "Speed-to-lead, in-house", w: 420, tone: "green",
      sub: "retires Callingly — later, once the spine is trusted",
      tags: [{ t: "program", k: "program" }, { t: "automated", k: "auto" }] }),
    N("s7", "adcard", 1440, 860, { label: "Capacity model + schedule solver", w: 420, tone: "yellow",
      sub: "real availability, no overbooking, after-hours honest",
      tags: [{ t: "program", k: "program" }, { t: "collaborative", k: "collab" }] }),
    N("s8", "adcard", 1440, 980, { label: "Running-late handoff", w: 420, tone: "purple",
      sub: "drafts the message to the customer, a person sends it",
      tags: [{ t: "agent", k: "agent" }, { t: "collaborative", k: "collab" }] }),
    N("s9", "adcard", 1440, 1100, { label: "The commission run", w: 420, tone: "green",
      sub: "computes the cycle, flags the last 10%, one approval, export",
      tags: [{ t: "program", k: "program" }, { t: "collaborative", k: "collab" }] }),
    N("s10", "adcard", 1440, 1230, { label: "Call visibility", w: 420, tone: "gate",
      sub: "only as far as the tracking layer can actually see — this is question 4",
      tags: [{ t: "program", k: "program" }, { t: "conditional", k: "cond" }] }),
    N("s11", "adcard", 1440, 1360, { label: "The gate + per-person permissions", w: 420, tone: "orange",
      sub: "one write path, draft-first, access to what someone needs and no more, with training that stays with them",
      tags: [{ t: "program", k: "program" }, { t: "collaborative", k: "collab" }] }),

    // legend
    N("leg", "adflow", 1440, 1490, { text: "How to read the tags", sub: "— program = can only ever do its one action · agent = judgement · collaborative = a human is in the loop" }, 6),
  ];

  const edges: Edge[] = [
    ...["p1", "p2", "p3"].map((p, i) => E(`a${i}`, p, "r1")),
    ...["p4", "p5", "p6", "p7", "p8", "p9"].map((p, i) => E(`b${i}`, p, "r2")),
    ...["p10", "p11", "p12", "p13", "p14", "p15"].map((p, i) => E(`c${i}`, p, "r3")),
    ...["p16", "p17"].map((p, i) => E(`d${i}`, p, "r4", { dark: true })),
    ...["p18", "p19", "p20"].map((p, i) => E(`e${i}`, p, "r5")),
    E("s1", "r1", "s1"), E("s2", "r1", "s2"), E("s3", "r1", "s3"),
    E("s4", "r2", "s4"), E("s5", "r2", "s5"), E("s6", "r2", "s6"),
    E("s7", "r3", "s7"), E("s8", "r3", "s8"), E("s9", "r3", "s9"),
    E("s10", "r4", "s10", { dark: true, dash: true, text: "only if reachable" }),
    E("s11", "r5", "s11"),
  ];
  return { nodes, edges };
}
